import React, { useState, useEffect, useMemo } from 'react';
import { X, BarChart3, Users, Euro, TrendingDown, Download } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line,
} from 'recharts';
import { fetchTariffOverrides, resolvePriceForMonth, type TariffOverride } from '../utils/tariffOverrides';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const normalizeFileName = (name: string): string =>
  name.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/['']/g, '').replace(/[^a-zA-Z0-9_-]/g, '_')
    .replace(/_+/g, '_').replace(/^_|_$/g, '');

interface GroupReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupName: string;
  participants: any[];
  initialYear?: string;
}

const AVAILABLE_YEARS = ['2024', '2025', '2026'];
const DEFAULT_REFERENCE_TARIFF = 160;
const SHARED_ENERGY_PRICE = 80;
const NETWORK_SAVINGS_RATE = 21;

interface IndexationValues {
  [month: string]: number;
}

function getContractDescription(p: any): string {
  if (p.contract_formula_type === 'fixed') {
    if (p.tariff_type === 'bi') {
      const hp = p.commodity_rate_hp || p.commodity_rate;
      const hc = p.commodity_rate_hc || p.commodity_rate;
      return `Fixe bihoraire — HP: ${hp ?? '—'} / HC: ${hc ?? '—'} €/MWh`;
    }
    return `Fixe: ${p.commodity_rate ?? '—'} €/MWh`;
  }
  if (p.contract_formula_type === 'variable') {
    const param = p.formula_x_param || 'X';
    const c = p.formula_c || 0;
    if (p.tariff_type === 'bi') {
      return `Variable bihoraire — HP: ${p.formula_a1 || 0}×${param}+${p.formula_b1 || 0}+${c} | HC: ${p.formula_a2 || 0}×${param}+${p.formula_b2 || 0}+${c}`;
    }
    return `Variable: ${p.formula_a || 0}×${param}+${p.formula_b || 0}+${c}`;
  }
  return 'Aucune info';
}

function calculateSupplierTariffForParticipant(
  p: any,
  monthKey: string,
  idxValues: IndexationValues,
  isHP: boolean = true
): number {
  if (p.contract_formula_type === 'fixed') {
    if (p.tariff_type === 'bi' && isHP) {
      return p.commodity_rate_hp || p.commodity_rate || DEFAULT_REFERENCE_TARIFF;
    }
    if (p.tariff_type === 'bi' && !isHP) {
      return p.commodity_rate_hc || p.commodity_rate || DEFAULT_REFERENCE_TARIFF;
    }
    return p.commodity_rate || DEFAULT_REFERENCE_TARIFF;
  }

  if (p.contract_formula_type === 'variable') {
    const xValue = idxValues[monthKey];
    if (xValue === undefined) return DEFAULT_REFERENCE_TARIFF;

    const c = parseFloat(p.formula_c) || 0;

    if (p.tariff_type === 'mono') {
      const a = parseFloat(p.formula_a) || 0;
      const b = parseFloat(p.formula_b) || 0;
      return a * xValue + b + c;
    } else {
      if (isHP) {
        const a1 = parseFloat(p.formula_a1) || 0;
        const b1 = parseFloat(p.formula_b1) || 0;
        return a1 * xValue + b1 + c;
      } else {
        const a2 = parseFloat(p.formula_a2) || 0;
        const b2 = parseFloat(p.formula_b2) || 0;
        return a2 * xValue + b2 + c;
      }
    }
  }

  return DEFAULT_REFERENCE_TARIFF;
}

function calculateSharedEnergyPriceForParticipant(
  p: any,
  monthKey: string,
  seIdxVals: IndexationValues,
  overrides: TariffOverride[]
): number {
  if (p.shared_energy_formula_type === 'variable' && p.shared_energy_a && p.shared_energy_x_param) {
    const a = parseFloat(p.shared_energy_a) || 0;
    const xVal = seIdxVals[monthKey];
    if (xVal !== undefined) return a * xVal;
  }
  const participantOverrides = overrides.filter(o => o.price_type === 'shared_energy_price');
  return resolvePriceForMonth(p.shared_energy_price ?? null, participantOverrides, monthKey);
}

export function GroupReportModal({ isOpen, onClose, groupName, participants, initialYear }: GroupReportModalProps) {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedYear, setSelectedYear] = useState<string>(initialYear || new Date().getFullYear().toString());
  const [monthlyTotals, setMonthlyTotals] = useState<any[]>([]);
  const [participantRows, setParticipantRows] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen && groupName) {
      loadGroupData();
    }
  }, [isOpen, groupName, selectedYear]);

  const loadIndexationValues = async (paramName: string): Promise<IndexationValues> => {
    try {
      const { data, error } = await supabase
        .from('indexation_parameters')
        .select('month_key, value')
        .eq('param_name', paramName);
      if (error) throw error;
      const values: IndexationValues = {};
      for (const row of data || []) {
        values[row.month_key] = Number(row.value);
      }
      return values;
    } catch {
      return {};
    }
  };

  const loadGroupData = async () => {
    setLoading(true);
    try {
      // Pre-load indexation values for all unique param names
      const paramNames = new Set<string>();
      const seParamNames = new Set<string>();

      for (const p of participants) {
        if (p.contract_formula_type === 'variable' && p.formula_x_param) {
          paramNames.add(p.formula_x_param);
        }
        if (p.shared_energy_formula_type === 'variable' && p.shared_energy_x_param) {
          seParamNames.add(p.shared_energy_x_param);
        }
      }

      const allIdxPromises: Promise<{ name: string; values: IndexationValues }>[] = [];
      for (const name of paramNames) {
        allIdxPromises.push(loadIndexationValues(name).then(v => ({ name, values: v })));
      }
      for (const name of seParamNames) {
        allIdxPromises.push(loadIndexationValues(name).then(v => ({ name, values: v })));
      }

      const allIdxResults = await Promise.all(allIdxPromises);
      const idxMap: Record<string, IndexationValues> = {};
      for (const r of allIdxResults) {
        idxMap[r.name] = r.values;
      }

      // Load tariff overrides for all participants
      const allOverrides: Record<string, TariffOverride[]> = {};
      for (const p of participants) {
        if (p.id) {
          try {
            allOverrides[p.id] = await fetchTariffOverrides(p.id);
          } catch {
            allOverrides[p.id] = [];
          }
        }
      }

      const monthMap: Record<string, {
        volumePartage: number;
        volumeComplementaire: number;
        injectionPartagee: number;
        injectionComplementaire: number;
        count: number;
        percentSum: number;
        savings: number;
        networkSavings: number;
      }> = {};
      const rows: any[] = [];

      for (const p of participants) {
        const pIdxValues = p.contract_formula_type === 'variable' && p.formula_x_param
          ? (idxMap[p.formula_x_param] || {})
          : {};
        const pSeIdxValues = p.shared_energy_formula_type === 'variable' && p.shared_energy_x_param
          ? (idxMap[p.shared_energy_x_param] || {})
          : {};
        const pOverrides = allOverrides[p.id] || [];

        let data: any = p.monthly_data;
        if (!data) {
          rows.push({ participant: p, sharedEnergyPrice: p.shared_energy_price, contractDesc: getContractDescription(p) });
          continue;
        }
        try {
          data = typeof data === 'string' ? JSON.parse(data) : data;
        } catch {
          rows.push({ participant: p, sharedEnergyPrice: p.shared_energy_price, contractDesc: getContractDescription(p) });
          continue;
        }

        const yearEntries = Object.entries(data).filter(([m]) => m.startsWith(selectedYear));
        const isProducer = p.type === 'producer';

        // Check billing_data for transport ELIA
        let hasZeroTransportElia = false;
        const yearMonths = yearEntries.map(([m]) => m).sort();
        const lastMonth = yearMonths[yearMonths.length - 1];
        if (lastMonth && p.billing_data) {
          try {
            const billing = typeof p.billing_data === 'string' ? JSON.parse(p.billing_data) : p.billing_data;
            const lastBilling = billing[lastMonth];
            const transportElia = lastBilling?.networkCosts?.transportELIA;
            hasZeroTransportElia = transportElia !== undefined && Number(transportElia) === 0;
          } catch { /* ignore */ }
        }

        for (const [month, vals] of yearEntries) {
          const vp = Number(vals.volume_partage || 0);
          const vc = Number(vals.volume_complementaire || 0);
          const ip = Number(vals.injection_partagee || 0);
          const ic = Number(vals.injection_complementaire || 0);
          const totalConso = vp + vc;
          const totalInj = ip + ic;
          const pct = isProducer ? (totalInj > 0 ? (ip / totalInj) * 100 : 0) : (totalConso > 0 ? (vp / totalConso) * 100 : 0);

          const sharedMWh = isProducer ? ip / 1000 : vp / 1000;
          const sharedEnergyPrice = calculateSharedEnergyPriceForParticipant(p, month, pSeIdxValues, pOverrides);

          let monthSavings = 0;
          if ((p.contract_formula_type === 'variable' || p.contract_formula_type === 'fixed') && p.tariff_type === 'bi') {
            const tariffHP = calculateSupplierTariffForParticipant(p, month, pIdxValues, true);
            const tariffHC = calculateSupplierTariffForParticipant(p, month, pIdxValues, false);
            const hpWeight = 5 / 7;
            const hcWeight = 2 / 7;
            const weightedTariff = tariffHP * hpWeight + tariffHC * hcWeight;
            monthSavings = sharedMWh * (weightedTariff - sharedEnergyPrice);
          } else {
            const tariff = calculateSupplierTariffForParticipant(p, month, pIdxValues, true);
            monthSavings = sharedMWh * (tariff - sharedEnergyPrice);
          }

          const netSavings = hasZeroTransportElia ? sharedMWh * NETWORK_SAVINGS_RATE : 0;

          if (!monthMap[month]) {
            monthMap[month] = {
              volumePartage: 0, volumeComplementaire: 0, injectionPartagee: 0,
              injectionComplementaire: 0, count: 0, percentSum: 0, savings: 0, networkSavings: 0,
            };
          }
          monthMap[month].volumePartage += vp;
          monthMap[month].volumeComplementaire += vc;
          monthMap[month].injectionPartagee += ip;
          monthMap[month].injectionComplementaire += ic;
          monthMap[month].count += 1;
          monthMap[month].percentSum += pct;
          monthMap[month].savings += monthSavings;
          monthMap[month].networkSavings += netSavings;
        }

        rows.push({ participant: p, sharedEnergyPrice: p.shared_energy_price, contractDesc: getContractDescription(p) });
      }

      const monthArr = Object.entries(monthMap)
        .map(([month, v]) => ({
          month: format(new Date(month + '-01'), 'MMMM', { locale: fr }),
          monthKey: month,
          totalConso: v.volumePartage + v.volumeComplementaire,
          totalInjection: v.injectionPartagee + v.injectionComplementaire,
          volumePartage: v.volumePartage,
          injectionPartagee: v.injectionPartagee,
          avgPercent: v.count > 0 ? v.percentSum / v.count : 0,
          savings: v.savings,
          networkSavings: v.networkSavings,
          totalSavings: v.savings + v.networkSavings,
        }))
        .sort((a, b) => a.monthKey.localeCompare(b.monthKey));

      setMonthlyTotals(monthArr);
      setParticipantRows(rows);
    } catch (error) {
      console.error('Erreur chargement rapport groupe:', error);
      toast.error('Erreur lors du chargement du rapport');
    } finally {
      setLoading(false);
    }
  };

  const totals = useMemo(() => {
    const totalConso = monthlyTotals.reduce((s, m) => s + m.totalConso, 0);
    const totalInjection = monthlyTotals.reduce((s, m) => s + m.totalInjection, 0);
    const avgPct = monthlyTotals.length > 0 ? monthlyTotals.reduce((s, m) => s + m.avgPercent, 0) / monthlyTotals.length : 0;
    const totalSavings = monthlyTotals.reduce((s, m) => s + m.savings, 0);
    const totalNetworkSavings = monthlyTotals.reduce((s, m) => s + m.networkSavings, 0);
    return { totalConso, totalInjection, avgPct, totalSavings, totalNetworkSavings, grandTotal: totalSavings + totalNetworkSavings };
  }, [monthlyTotals]);

  const handleExportPDF = async () => {
    setExporting(true);
    const toastId = toast.loading('Préparation du document...');

    let buttonsToHide: NodeListOf<Element> | null = null;

    try {
      const reportElement = document.getElementById('group-report-content');

      if (!reportElement) {
        toast.error('Contenu du rapport introuvable', { id: toastId });
        setExporting(false);
        return;
      }

      buttonsToHide = reportElement.querySelectorAll('.no-print');
      buttonsToHide.forEach((btn) => {
        (btn as HTMLElement).style.visibility = 'hidden';
      });

      await new Promise(resolve => setTimeout(resolve, 200));

      const canvas = await html2canvas(reportElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowHeight: reportElement.scrollHeight,
        height: reportElement.scrollHeight,
        scrollX: 0,
        scrollY: 0,
      });

      if (buttonsToHide) {
        buttonsToHide.forEach((btn) => {
          (btn as HTMLElement).style.visibility = 'visible';
        });
      }

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const margin = 5;

      const pdfWidth = 210;
      const usableWidth = pdfWidth - (margin * 2);
      const ratio = usableWidth / imgWidth;
      const scaledHeight = imgHeight * ratio;
      const pdfHeight = scaledHeight + (margin * 2);

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [pdfWidth, pdfHeight],
        compress: true,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      pdf.addImage(imgData, 'JPEG', margin, margin, usableWidth, scaledHeight);

      const cleanName = normalizeFileName(groupName);
      const fileName = `rapport_groupe_${cleanName}_${selectedYear}.pdf`;
      pdf.save(fileName);

      toast.success('Rapport de groupe exporté avec succès', { id: toastId });
    } catch (error: any) {
      console.error('Erreur export PDF:', error);
      toast.error(`Erreur lors de l'export: ${error.message}`, { id: toastId });
      if (buttonsToHide) {
        buttonsToHide.forEach((btn) => {
          (btn as HTMLElement).style.visibility = 'visible';
        });
      }
    } finally {
      setExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div id="group-report-content" className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-neutral-900">Rapport de groupe — {groupName}</h2>
                <p className="text-sm text-neutral-500">{participants.length} participants — Année {selectedYear}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 no-print">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                {AVAILABLE_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <button
                onClick={handleExportPDF}
                disabled={exporting || loading}
                className="inline-flex items-center px-4 py-2 text-white rounded-lg disabled:opacity-50 transition-all duration-300"
                style={{ backgroundColor: '#F8BD3D', color: '#1a1a1a' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E5A935'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F8BD3D'}
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Export...' : 'Exporter PDF'}
              </button>
              <button onClick={onClose} className="text-neutral-500 hover:text-neutral-700 transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
              <p className="mt-4 text-neutral-500">Chargement du rapport de groupe…</p>
            </div>
          ) : monthlyTotals.length === 0 ? (
            <div className="text-center py-16 text-neutral-500">Aucune donnée disponible pour {selectedYear}</div>
          ) : (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-teal-50 rounded-xl p-5 border border-teal-100">
                  <div className="flex items-center mb-2"><BarChart3 className="w-5 h-5 text-teal-600 mr-2" /><span className="text-sm font-medium text-teal-700">Consommation totale</span></div>
                  <p className="text-2xl font-bold text-teal-900">{(totals.totalConso / 1000).toFixed(2)} MWh</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-5 border border-amber-100">
                  <div className="flex items-center mb-2"><BarChart3 className="w-5 h-5 text-amber-600 mr-2" /><span className="text-sm font-medium text-amber-700">Injection totale</span></div>
                  <p className="text-2xl font-bold text-amber-900">{(totals.totalInjection / 1000).toFixed(2)} MWh</p>
                </div>
                <div className="bg-green-50 rounded-xl p-5 border border-green-100">
                  <div className="flex items-center mb-2"><Euro className="w-5 h-5 text-green-600 mr-2" /><span className="text-sm font-medium text-green-700">% conso couverte (moyenne)</span></div>
                  <p className="text-2xl font-bold text-green-900">{totals.avgPct.toFixed(1)}%</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
                  <div className="flex items-center mb-2"><TrendingDown className="w-5 h-5 text-blue-600 mr-2" /><span className="text-sm font-medium text-blue-700">Économies totales</span></div>
                  <p className="text-2xl font-bold text-blue-900">{totals.grandTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</p>
                </div>
              </div>

              {/* Chart: total consumption evolution */}
              <div className="bg-white rounded-xl border border-neutral-200 p-6">
                <h3 className="text-lg font-semibold text-neutral-900 mb-4">Évolution de la conso totale du groupe</h3>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={monthlyTotals}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" stroke="#6B7280" tick={{ fontSize: 11 }} />
                      <YAxis stroke="#6B7280" tick={{ fontSize: 11 }} unit=" kWh" />
                      <Tooltip formatter={(v: number) => `${v.toLocaleString()} kWh`} contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                      <Legend />
                      <Line type="monotone" dataKey="totalConso" stroke="#0d9488" name="Consommation totale" strokeWidth={2} dot={{ r: 4 }} />
                      <Line type="monotone" dataKey="totalInjection" stroke="#f59e0b" name="Injection totale" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Chart: average coverage % */}
              <div className="bg-white rounded-xl border border-neutral-200 p-6">
                <h3 className="text-lg font-semibold text-neutral-900 mb-4">Pourcentage de conso couverte (moyenne du groupe)</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyTotals}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" stroke="#6B7280" tick={{ fontSize: 11 }} />
                      <YAxis stroke="#6B7280" tick={{ fontSize: 11 }} unit="%" />
                      <Tooltip formatter={(v: number) => `${v.toFixed(1)}%`} contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }} />
                      <Bar dataKey="avgPercent" fill="#10B981" name="% couverture moyen" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Savings table */}
              <div className="bg-white rounded-xl border border-neutral-200 p-6">
                <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                  <TrendingDown className="w-5 h-5 text-blue-600 mr-2" />
                  Économies mensuelles du groupe
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-neutral-200">
                        <th className="text-left py-2 px-3 font-medium text-neutral-600">Mois</th>
                        <th className="text-right py-2 px-3 font-medium text-neutral-600">Énergie partagée (kWh)</th>
                        <th className="text-right py-2 px-3 font-medium text-neutral-600">Économies énergie (€)</th>
                        <th className="text-right py-2 px-3 font-medium text-neutral-600">Économies réseau (€)</th>
                        <th className="text-right py-2 px-3 font-medium text-neutral-600">Total économies (€)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyTotals.map((m, i) => (
                        <tr key={i} className="border-b border-neutral-100">
                          <td className="py-2 px-3 text-neutral-900 font-medium capitalize">{m.month}</td>
                          <td className="py-2 px-3 text-right text-neutral-600">{(m.volumePartage + m.injectionPartagee).toLocaleString('fr-FR')}</td>
                          <td className="py-2 px-3 text-right text-neutral-600">{m.savings.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="py-2 px-3 text-right text-neutral-600">{m.networkSavings > 0 ? m.networkSavings.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</td>
                          <td className="py-2 px-3 text-right text-blue-600 font-semibold">{m.totalSavings.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-neutral-300">
                        <td className="py-3 px-3 font-bold text-neutral-900">Total annuel</td>
                        <td className="py-3 px-3 text-right font-bold text-neutral-900">{(monthlyTotals.reduce((s, m) => s + m.volumePartage + m.injectionPartagee, 0)).toLocaleString('fr-FR')}</td>
                        <td className="py-3 px-3 text-right font-bold text-neutral-900">{totals.totalSavings.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="py-3 px-3 text-right font-bold text-neutral-900">{totals.totalNetworkSavings > 0 ? totals.totalNetworkSavings.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</td>
                        <td className="py-3 px-3 text-right font-bold text-blue-700">{totals.grandTotal.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Detail table: per participant */}
              <div className="bg-white rounded-xl border border-neutral-200 p-6">
                <h3 className="text-lg font-semibold text-neutral-900 mb-4">Détail des calculs par participant</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-neutral-200">
                        <th className="text-left py-2 px-3 font-medium text-neutral-600">Participant</th>
                        <th className="text-left py-2 px-3 font-medium text-neutral-600">Type</th>
                        <th className="text-left py-2 px-3 font-medium text-neutral-600">Contrat fournisseur</th>
                        <th className="text-left py-2 px-3 font-medium text-neutral-600">Formule / Prix fixe</th>
                        <th className="text-right py-2 px-3 font-medium text-neutral-600">Prix Sun Is Up (€/MWh)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {participantRows.map((row, i) => {
                        const p = row.participant;
                        const contractType = p.contract_formula_type === 'fixed' ? 'Fixe' : p.contract_formula_type === 'variable' ? 'Variable' : '—';
                        return (
                          <tr key={i} className="border-b border-neutral-100">
                            <td className="py-2 px-3 text-neutral-900 font-medium">{p.name}</td>
                            <td className="py-2 px-3 text-neutral-600">{p.type === 'producer' ? 'Producteur' : p.type === 'prosumer' ? 'Prosumer' : 'Consommateur'}</td>
                            <td className="py-2 px-3 text-neutral-600">{contractType}{p.tariff_type === 'bi' ? ' (bihoraire)' : ''}</td>
                            <td className="py-2 px-3 text-neutral-600 text-xs">{row.contractDesc}</td>
                            <td className="py-2 px-3 text-right text-amber-600 font-medium">{p.shared_energy_price ?? '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-neutral-50 rounded-xl p-4 text-xs text-neutral-500">
                <p>Rapport de groupe généré le {format(new Date(), 'dd/MM/yyyy à HH:mm', { locale: fr })} pour le groupe « {groupName} » ({participants.length} participants).</p>
                <p className="mt-1">Les économies sont calculées comme la différence entre le tarif fournisseur et le prix Sun Is Up, appliquée au volume d'énergie partagée. Pour les contrats bihoraires, une pondération 5/7 (heure pleine) et 2/7 (heure creuse) est utilisée.</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
